/* eslint-disable array-callback-return */
import {
    Accidental,
    BarlineType,
    Flow,
    FormatParams,
    Formatter,
    RenderContext,
    Renderer,
    StaveConnector,
    Stem,
    Tickable,
    TickContext,
    Voice,
} from 'vexflow4';
import { Position, TouchListener } from './touchListener';
import { NoteMap } from './noteMap';
import {
    checkCurrentNote,
    checkCurrentStave, findNextIndexes, findPreviousIndexes,
    findWhichStaveMouseOver,
    formatStavesToJson,
    getMoveKeysFunc,
    groupSequence,
    noteEnharmonicByAcc,
    noteMatchAcc,
    removeEmptyArrays, shuffleArray, simplifyRelation
} from '../utils/helpers';
import { PROVISORY_TICKABLE_STYLE } from './constants';
import { StaveNote } from './extension/MusiconStaveNote';
import Stave from './extension/MusiconStave';
import Player, { ClefType, PlayInfo } from './player';
import { BarNote } from './extension/MusiconBarNote';
import {
    addDirection,
    checkParallel,
    chordDetect,
    findDownDirectionSeventhNotes,
    findPreviousTwoNote,
    findUpDirectionSeventhNotes,
    generate358IntervalNote,
    generate58IntervalNote,
    generateStepNote,
    handleHiddenOctaveFifth,
    handleJumping,
    handleParallelFifth,
    handleParallelOctave,
    seventhNoteProcessing,
    staveDataTransfer
} from '../utils/noteTransfer';
import { distance, Interval, Key, Note, transpose } from 'tonal';
import { accNotesType, chordMatchingBarType, chordNoteBarType, chordNoteType, detuningInfoType, } from '../utils/types';
import { Toast } from 'antd-mobile';
import { doPostMessage } from '../utils/rn';

const CANVAS_HEIGHT = 260;
const LEFT_PADDING = 60;
const STAVE_WIDTH = 250;
const HEAD_STAVE_WIDTH = 75;
const MELODY_TOP = 20;
const CHORD_TOP = 100 + MELODY_TOP;

const MELODY_KEY_RANGE = { max: 'G/6', min: 'B/2' };
const CHORD_KEY_RANGE = { max: 'B/6', min: 'B/2' };

export const SCORE_STORE_KEY = 'local-music-score';

// console.log(Note.enharmonic('E3'))
// console.log(distance('F3', 'Db4'))
// console.log(transpose('Db4', '-6M'))
// console.log(distance('G#4', 'C5'))
// const interval = Interval.get(distance('G#3', 'C4'));
// console.log(Interval.simplify(Interval.fromSemitones(interval.semitones || 0)))

export class Handler {
    static instance: Handler;
    private render: Renderer;
    private ctx: RenderContext;
    private staveList: Stave[] = [];
    private melodyNoteList: StaveNote[] = [];
    private chordNoteList: StaveNote[] = [];
    private title: string = '';

    private melodyHeadStave = new Stave(LEFT_PADDING, MELODY_TOP, HEAD_STAVE_WIDTH);
    private melodyStaveList: Stave[] = [];

    private chordHeadStave = new Stave(LEFT_PADDING, CHORD_TOP, HEAD_STAVE_WIDTH);
    private chordStaveList: Stave[] = [];

    public currentStave: Stave | null = null;
    public currentNote: StaveNote | null = null;

    private provisoryTickable: StaveNote | null = null;
    public duration: string = '4';

    private preProvisoryTickable: StaveNote | null = null;

    private dragCursorX: number | null = null;
    private currentNoteCursorX: number | null = null;

    private noteUpStatus: number = 0;
    public afterInsertNoteUpStatusCallback: () => void = () => {
    };

    private noteDownStatus: number = 0;
    public afterInsertNoteDownStatusCallback: () => void = () => {
    };

    private noteDoubleUpStatus: number = 0;
    public afterInsertNoteDoubleUpStatusCallback: () => void = () => {
    };

    private noteDoubleDownStatus: number = 0;
    public afterInsertNoteDoubleDownStatusCallback: () => void = () => {
    };

    private renoteStatus: number = 0;
    public afterInsertRenoteStatusCallback: () => void = () => {
    };

    private player: Player;
    private chordPlayer: Player;

    private currentBarIndex: number | undefined = undefined;
    // i.e {bar_0: 4, bar_1: 2, bar_2: 1}
    private melodyNumber: { [key: string]: number } = {};
    private chordNumber: { [key: string]: number } = {};

    private detuningInfo: detuningInfoType[] = [];
    private accNotes: accNotesType[] = [];
    private seventhNotes: any = {};
    private melodyNum: number = 1;
    private chordNum: number = 1;
    private chordSolveHelper: any = {}

    public playing: boolean = false;

    public static getInstance(render: Renderer): Handler {
        if (!Handler.instance) Handler.instance = new Handler(render);
        return Handler.instance;
    }

    private constructor(render: Renderer) {
        this.render = render;
        this.ctx = render.getContext();
        this.player = new Player(this);
        this.chordPlayer = new Player(this);
    }

    public init = () => {
        try {
            this.ctx.clear();
            const scoreString = localStorage.getItem(SCORE_STORE_KEY);

            doPostMessage({ score: scoreString }, 'debug');

            let score = undefined;

            if (scoreString != null) {
                score = JSON.parse(scoreString);
            }

            this.restoreScore(score);
            this.redraw();
            this.restoreScore(score);
            this.redraw();
        } catch (e) {
            console.log(e);
        }
    };

    public getContext = (): RenderContext => this.ctx;
    public getStaveList = (): Stave[] => this.staveList;
    public getNoteList = (): StaveNote[] => this.melodyNoteList;
    public getMelodyStaveList = (): Stave[] => this.melodyStaveList;
    public getChordStaveList = (): Stave[] => this.chordStaveList;

    public getCurrentStave = (): Stave | null => this.currentStave;
    public setCurrentStave = (stave: Stave | null) => {
        this.currentStave = stave;

        if (stave === null) {
            this.dragCursorX = null;
        }
    };

    public getCurrentNote = (): StaveNote | null => this.currentNote;
    public setCurrentNote = (note: StaveNote | null) => {
        this.currentNote = note;

        try {
            this.currentNoteCursorX = note?.getAbsoluteX() ?? null;
        } catch (e) {
        }
    };

    public getDragCursorX = (): number | null => this.dragCursorX;
    public setDragCursorX = (x: number | null) => {
        this.dragCursorX = x;
    };

    public getProvisoryTickable = (): StaveNote | null => this.preProvisoryTickable;

    public setProvisoryTickable = (note: StaveNote | null) => {
        this.preProvisoryTickable = this.provisoryTickable;
        this.provisoryTickable = note;
    };

    public getNoteUpStatus = (): number => this.noteUpStatus;
    public setNoteUpStatus = (status: number) => {
        this.noteUpStatus = status;
    };

    public getNoteDownStatus = (): number => this.noteDownStatus;
    public setNoteDownStatus = (status: number) => {
        this.noteDownStatus = status;
    };

    public getNoteDoubleUpStatus = (): number => this.noteDoubleUpStatus;
    public setNoteDoubleUpStatus = (status: number) => {
        this.noteDoubleUpStatus = status;
    };

    public getNoteDoubleDownStatus = (): number => this.noteDoubleDownStatus;
    public setNoteDoubleDownStatus = (status: number) => {
        this.noteDoubleDownStatus = status;
    };

    public getRenoteStatus = (): number => this.renoteStatus;
    public setRenoteStatus = (status: number) => {
        this.renoteStatus = status;
    };

    public getScoreData = (): string => {
        const scoreData = localStorage.getItem(SCORE_STORE_KEY);

        if (!scoreData) {
            return '';
        }

        return scoreData;
    };

    public addEmptyStaves = (num: number = 1) => {
        for (let i = 0; i < num; i++) {
            const [newMelodyStave, newChordStave] = this.getEmptyStave();

            this.melodyStaveList.push(newMelodyStave);
            this.chordStaveList.push(newChordStave);
        }

        this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
    };

    public redraw = () => {
        this.ctx.clear();
        this._drawHeads();
        this._drawDragCursor();
        this._drawNoteCursor();
        this._drawStaves();
        this._drawNotes();
        this._drawProvisoryTickable();
    };

    public startListenTouchEvent(canvas: HTMLCanvasElement | null) {
        if (!canvas) return;
        const listener = TouchListener.getInstance(canvas, this.render);
        listener.startListening();
    }

    public stopListenTouchEvent = (canvas: HTMLCanvasElement | null) => {
        if (!canvas) return;
        const listener = TouchListener.getInstance(canvas, this.render);
        listener.stopListening();
    };

    public getNextStave = (stave: Stave) => {
        const box = stave.getBoundingBox();

        const mousePos = {
            x: box.x + box.w + 1,
            y: stave.getTopLineTopY(),
        };

        const next = findWhichStaveMouseOver(this.staveList, mousePos);

        return next;
    };

    private getEmptyStave = () => {
        let lastMelody = this.melodyHeadStave;

        if (this.melodyStaveList.length > 0) {
            lastMelody = this.melodyStaveList[this.melodyStaveList.length - 1];
        }

        const mbox = lastMelody.getBoundingBox();

        const x = mbox.getX() + mbox.getW();
        const newMelodyStave = new Stave(x, MELODY_TOP, STAVE_WIDTH);
        newMelodyStave.setBegBarType(BarlineType.NONE);

        const newChordStave = new Stave(x, CHORD_TOP, STAVE_WIDTH);
        newChordStave.setBegBarType(BarlineType.NONE);

        return [newMelodyStave, newChordStave];
    };

    private _drawHeads = () => {
        const tone_pitch = localStorage.getItem('tone_pitch');
        const headStaveWidth = tone_pitch === 'none' || tone_pitch === 'C' ? 90 : 140;
        const mhs = new Stave(LEFT_PADDING, MELODY_TOP, 80);
        mhs.addClef('treble');
        mhs.addTimeSignature('4/4');
        mhs.setWidth(headStaveWidth);
        mhs.setEndBarType(7);

        const chs = new Stave(LEFT_PADDING, CHORD_TOP, 80);
        chs.addClef('bass');
        chs.addTimeSignature('4/4');
        chs.setWidth(headStaveWidth);
        chs.setEndBarType(7);

        // 添加调式
        if (tone_pitch && tone_pitch !== 'none') {
            mhs.setKeySignature(tone_pitch);
            chs.setKeySignature(tone_pitch);
        }
        mhs.setContext(this.ctx).draw();
        chs.setContext(this.ctx).draw();
        this.chordHeadStave = chs;
        this.melodyHeadStave = mhs;

        const BraceConnector = new StaveConnector(this.melodyHeadStave, this.chordHeadStave);
        BraceConnector.setType(StaveConnector.type.BRACE);
        BraceConnector.setContext(this.ctx).draw();

        const singleLeftConnector = new StaveConnector(this.melodyHeadStave, this.chordHeadStave);

        singleLeftConnector.setType(StaveConnector.type.SINGLE_LEFT);
        singleLeftConnector.setContext(this.ctx).draw();
    };

    private _drawStaves = () => {
        this.melodyStaveList.forEach((stave: Stave, i: number) => {
            stave.setContext(this.ctx).draw();

            const rightLineConnector = new StaveConnector(stave, new Stave(stave.getX(), CHORD_TOP, STAVE_WIDTH));
            rightLineConnector.setType(StaveConnector.type.SINGLE_RIGHT);
            rightLineConnector.setContext(this.ctx).draw();
        });
        this.chordStaveList.forEach((stave: Stave, i: number) => {
            stave.setContext(this.ctx).draw();
        });
    };

    private _drawNotes = (stave?: Stave) => {
        if (stave === undefined) {
            this.staveList.forEach((stave) => {
                this._drawNotes(stave);
            });
        } else {
            const voice = new Voice(Flow.TIME4_4).setMode(Voice.Mode.SOFT);
            const notes = stave.getTickables().map((note: StaveNote) => {
                note.setCenterAlignment(false);
                note.setWidth(25);
                return note;
            });
            voice.addTickables(notes);

            const formatter = new Formatter();
            const formatOption = {
                align_rests: true,
                auto_beam: true,
                width: 50,
                minSpace: 50,
            } as FormatParams;

            formatter.joinVoices([voice]);

            if (stave.getTickables().length > 0) {
                formatter.formatToStave([voice], stave, formatOption);
            }
            voice.draw(this.ctx, stave);
        }
    };

    private _drawHarmonies = (stave?: Stave) => {
        if (stave === undefined) {
            this.staveList.forEach((stave) => {
                this._drawHarmonies(stave);
            });
        } else {
            const voice = new Voice(Flow.TIME4_4).setMode(Voice.Mode.SOFT);
            stave.getTickables().map((note: StaveNote) => {
                const voices: Tickable[] = [];
                note.getKeys().forEach((k, v) => {
                    voices.push(
                        new StaveNote({
                            keys: [k],
                            duration: '4',
                            stem_direction: v === 0 ? 1 : -1,
                        }),
                    );
                });
                voice.addTickables(voices);
            });

            const formatter = new Formatter();
            const formatOption = {
                align_rests: false,
                auto_beam: false,
                width: 0,
                minSpace: 50,
            } as FormatParams;

            formatter.joinVoices([voice]);

            if (stave.getTickables().length > 0) {
                formatter.formatToStave([voice], stave, formatOption);
            }
            formatter.format([voice], 0);
            voice.draw(this.ctx, stave);
        }
    };

    public updateProvisoryKey = (mousePos: Position) => {
        this.dragCursorX = mousePos.x;
        if (this.currentStave === null) {
            return;
        }

        if (this.provisoryTickable === null) {
            let newNote = new StaveNote({ keys: ['d/4'], duration: this.duration });

            this.setProvisoryTickable(newNote);
        }

        if (!(this.provisoryTickable instanceof StaveNote) || this.provisoryTickable?.getNoteType() === 'r') {
            //No need to update key if its not a note or if its a rest, just draw the tickable in the new mouse position
        } else {
            const noteName = NoteMap.getNoteName(this.currentStave, mousePos);

            if (noteName !== this.provisoryTickable.getKeys()[0]) {
                const cloneNote = this.provisoryTickable.clone({
                    keys: [noteName],
                });
                this.setProvisoryTickable(cloneNote);

                if (this.provisoryTickable) {
                    this.provisoryTickable.setStyle(PROVISORY_TICKABLE_STYLE);
                }
                this.provisoryTickable?.autoStem();
                this.provisoryTickable?.setStave(this.currentStave);
                this.provisoryTickable?.setTickContext(new TickContext());
            }
        }
        this.drawProvisoryTickable(mousePos);
    };

    public drawProvisoryTickable = (mousePos: Position) => {
        if (this.currentStave === null || this.provisoryTickable === null || !mousePos) return;

        if (this.provisoryTickable instanceof StaveNote) {
            //Fix X position to set exactly where the mouse is
            //TODO the -5 value shouldnt be absolute! it should reflect half the note's Width
            const xShift = mousePos.x - this.provisoryTickable.getAbsoluteX() - 5;
            this.provisoryTickable.setXShift(xShift);
        }

        this.dragCursorX = mousePos.x;

        this.redraw();
    };

    public isHeadStave = (pos: Position) => {
        return this.melodyHeadStave.getX() + this.melodyHeadStave.getWidth() >= pos.x;
    };

    private _drawNoteCursor = () => {
        const x = this.currentNoteCursorX;
        const stave = this.currentStave;

        this.currentBarIndex = undefined;

        if (!stave || !x) return;

        const space = stave.getSpacingBetweenLines();
        const y = stave.getTopLineTopY();
        const w = space * 2;
        const h = space * 4;

        const tempFillStyle = this.ctx.fillStyle;

        this.ctx.fillStyle = '#FDE3AF';
        this.ctx.fillRect(x - space / 2, y, w, h);
        // 找到当前选中的小节编号
        if (this.currentStave?.getY() === 20) {
            this.currentBarIndex = (this.currentStave?.getX() - 135) / 150;
        }

        this.ctx.fillStyle = tempFillStyle;
    };

    private _drawProvisoryTickable = () => {
        if (!this.currentStave) return;
        if (!this.provisoryTickable) return;

        //Only draw Provisory note if not on a definitive note
        const tempFillStyle = this.ctx.fillStyle;
        const tempStrokeStyle = this.ctx.strokeStyle;
        this.ctx.fillStyle = 'gray';
        this.ctx.strokeStyle = 'gray';

        this.provisoryTickable.setStave(this.currentStave);
        this.provisoryTickable.draw();

        this.ctx.fillStyle = tempFillStyle;
        this.ctx.strokeStyle = tempStrokeStyle;
    };

    private _drawDragCursor = () => {
        if (!this.dragCursorX || !this.currentStave) {
            return;
        }

        const tempFillStyle = this.ctx.fillStyle;
        this.ctx.fillStyle = 'rgba(255, 241, 212, 0.80)';

        const space = this.currentStave.getSpacingBetweenLines();
        const x = this.dragCursorX - space / 2;
        const y = 0;
        const w = space * 2;
        const h = 240;

        this.ctx.fillRect(x, y, w, h);

        this.ctx.fillStyle = tempFillStyle;
    };

    public getLastTickableBeforeXPosition = (x: number) => {
        return this._findNearStaveNote(x, true);
    };

    public getFirstTickableAfterXPosition = (x: number) => {
        return this._findNearStaveNote(x, false);
    };

    private _findNearStaveNote = (x: number, isPre: boolean = true) => {
        const stave = this.currentStave;
        const staveNotes = stave?.getTickables() ?? [];

        if (staveNotes.length === 0) {
            return null;
        }

        let nearNotes;

        if (isPre) {
            nearNotes = staveNotes.filter((n) => n.getAbsoluteX() < x);
            nearNotes.sort((a: StaveNote, b: StaveNote) => {
                return a.getAbsoluteX() - b.getAbsoluteX() < 0 ? 1 : -1;
            });
        } else {
            nearNotes = staveNotes.filter((n) => n.getAbsoluteX() > x);
            nearNotes.sort((a: StaveNote, b: StaveNote) => {
                return a.getAbsoluteX() - b.getAbsoluteX() > 0 ? 1 : -1;
            });
        }

        return nearNotes[0] ?? null;
    };

    public rebuildStaveList = (limitDuration = false) => {
        this.rebuildMelodyStaves(limitDuration);
        this.rebuildChordStaves(limitDuration);
        this.completeStaves();

        const score = {
            title: this.title,
            melodyStaves: formatStavesToJson(this.melodyStaveList),
            chordStaves: formatStavesToJson(this.chordStaveList),
        };

        localStorage.setItem(SCORE_STORE_KEY, JSON.stringify(score));

        // 重置画布大小
        const width = 300 + this.melodyStaveList.length * STAVE_WIDTH;
        this.render.resize(width, CANVAS_HEIGHT);
    };

    private rebuildMelodyStaves = (limitDuration = false) => {
        const sortStave = this.melodyStaveList.sort((a, b) => a.getX() - b.getX());

        const notes: StaveNote[] = [];
        sortStave.map((stave: Stave) => {
            return stave.getTickables().map((note: StaveNote) => {
                return notes.push(note);
            });
        });

        const staveNotes = groupSequence(notes, limitDuration);

        this.melodyNoteList = notes;

        const newStaves = staveNotes.map(this.noteToStave(this.melodyHeadStave));

        this.melodyStaveList = newStaves;
        this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
    };

    private rebuildChordStaves = (limitDuration = false) => {
        const sortStave = this.chordStaveList.sort((a, b) => a.getX() - b.getX());

        const notes: StaveNote[] = [];
        sortStave.map((stave: Stave) => {
            return stave.getTickables().map((note: StaveNote) => {
                return notes.push(note);
            });
        });
        this.chordNoteList = notes;
        const staveNotes = groupSequence(notes, limitDuration);

        const newStaves = staveNotes.map(this.noteToStave(this.chordHeadStave));

        this.chordStaveList = newStaves;
        this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
    };

    private completeStaves = () => {
        const melodyGroupNotes = groupSequence(this.melodyNoteList);
        const chordGroupNotes = groupSequence(this.chordNoteList);

        const maxLength = Math.max(melodyGroupNotes.length, chordGroupNotes.length);

        const group = [];

        for (let i = 0; i < maxLength + 5; i++) {
            group.push([]);
        }

        const melodyStaves = group.map(this.noteToStave(this.melodyHeadStave));
        const chordStaves = group.map(this.noteToStave(this.chordHeadStave));

        this.melodyStaveList = [...this.melodyStaveList, ...melodyStaves];
        this.chordStaveList = [...this.chordStaveList, ...chordStaves];

        this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
    };

    private noteToStave = (headStave: Stave) => (notes: StaveNote[], i: number) => {
        const x = headStave.getX() + headStave.getWidth() + STAVE_WIDTH * i;
        const y = headStave.getY();
        const w = STAVE_WIDTH;
        const stave = new Stave(x, y, w);
        let preNote: StaveNote | null = null;

        notes.map((note: StaveNote) => {
            stave.insertTickableBetween(note, preNote, null);
            preNote = note;
            return null;
        });

        stave.setBegBarType(7);

        return stave;
    };

    public cleanAll = () => {
        this.chordStaveList = [];
        this.melodyStaveList = [];
        this.melodyNoteList = [];
        this.staveList = [];
        this.addEmptyStaves(6);
        localStorage.removeItem(SCORE_STORE_KEY);
        localStorage.removeItem('init_tone_pitch');

        this.redraw();
    };

    public upAllKeys = () => {
        this.melodyStaveList = this.melodyStaveList.map(getMoveKeysFunc('up', this.currentNote, MELODY_KEY_RANGE));
        this.chordStaveList = this.chordStaveList.map(getMoveKeysFunc('up', this.currentNote, CHORD_KEY_RANGE));

        this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
        this.rebuildStaveList();
        this.redraw();
    };

    public downAllKeys = () => {
        this.melodyStaveList = this.melodyStaveList.map(getMoveKeysFunc('down', this.currentNote, MELODY_KEY_RANGE));
        this.chordStaveList = this.chordStaveList.map(getMoveKeysFunc('down', this.currentNote, CHORD_KEY_RANGE));

        this.staveList = [...this.melodyStaveList, ...this.chordStaveList];
        this.rebuildStaveList();
        this.redraw();
    };

    public deleteNote = () => {
        if (!this.currentNote) {
            // localStorage.removeItem('init_tone_pitch');
            //
            // this.melodyStaveList = [];
            // this.chordStaveList = [];
            // this.staveList = [];
            //
            // this.currentNoteCursorX = null;
            // this.rebuildStaveList();
            // this.redraw();
            //
            Toast.show({ content: '请先选中音符' });
            return;
        }

        const removeNote = (stave: Stave) => {
            if (!checkCurrentStave(stave, this.currentNote)) return stave;

            const newNotes = stave.getTickables().filter((note: StaveNote) => {
                return !checkCurrentNote(note, this.currentNote);
            });

            stave.setTickables(newNotes);

            return stave;
        };

        this.melodyStaveList.map(removeNote);
        this.chordStaveList.map(removeNote);

        this.currentNoteCursorX = null;
        this.currentNote = null;
        this.rebuildStaveList();
        this.redraw();
    };

    private restoreScore = (data: any) => {
        try {
            if (!data) {
                this.addEmptyStaves(5);
                return;
            }

            this.restoreStaves(data.melodyStaves, this.melodyStaveList);
            this.restoreStaves(data.chordStaves, this.chordStaveList);

            this.rebuildStaveList();
        } catch (e) {
            this.addEmptyStaves(5);
            console.error('解析数据失败', e);
            console.info('解析数据失败数据', data);
        }
    };

    /**
     *
     * @param staveData
     * @param staves
     * @returns
     */
    private restoreStaves = (staveData: any[], staves: Stave[]) => {
        if (!Array.isArray(staves) || !Array.isArray(staveData)) {
            return;
        }

        this.addEmptyStaves(staveData.length);

        staveData.map(({ notes }, i) => {
            const stave = staves[i];
            const newNotes = notes.map(
                ({
                    keys,
                    duration,
                    accidentals,
                    direction,
                    durations,
                }: {
                    keys: string[];
                    duration: string;
                    accidentals: string[];
                    direction: string[];
                    durations: string[];
                }) => {
                    const note = new StaveNote({ keys, duration });

                    let stemDirections = [];

                    if (direction && direction.length > 0) {
                        stemDirections = direction.map((d) => {
                            return d === 'up' ? Stem.UP : Stem.DOWN;
                        });

                        note.multipleStems = stemDirections;
                    }

                    if (durations && durations.length > 0) {
                        note.multipleDurations = durations;
                    }

                    note.autoStem();

                    accidentals.map((accidental: any): void => {
                        if (accidental === null) {
                            return;
                        }

                        if (typeof accidental === 'string') {
                            note.addModifier(new Accidental(accidental), 0);
                            return;
                        }

                        const index = accidental.index;
                        const accObj = new Accidental(accidental.modifier);
                        note.addModifier(accObj, index);
                    });

                    return note;
                },
            );
            stave.setTickables(newNotes);
        });
    };

    public play = (callback?: () => void) => {
        if (this.playing) return;

        this.playing = true;

        const onFinished = () => {
            this.playing = false;
        };

        const max = Math.max(this.melodyStaveList.length, this.chordStaveList.length);

        const melodyPlayer = this.playStaves(this.player, this.melodyStaveList, 'treble', () => {
            if (this.melodyStaveList.length === max) {
                onFinished();
                callback && callback();
            }
        });
        const chordPlayer = this.playStaves(this.chordPlayer, this.chordStaveList, 'bass', () => {
            if (this.chordStaveList.length === max) {
                onFinished();
                callback && callback();
            }
        });

        setTimeout(() => {
            melodyPlayer.play();
            chordPlayer.play();
        }, 300);
    };

    private playStaves = (player: Player, staves: Stave[], clef: ClefType, callback: () => void) => {
        //TODO RPM should be set outside...
        const rpm = 120;
        const playInfo: PlayInfo = {
            delay: 0,
            rpm: rpm,
            defaultTime: rpm / 60, // to seconds
            clef,
        };

        let playEvents: any[] = [];

        staves.map((stave: Stave) => {
            //set clef to playinfo
            playInfo.clef = clef;

            //Call initial barline play events
            const barNote = new BarNote();

            stave.getTickables().map((note: StaveNote) => {
                playEvents = playEvents.concat(note.getPlayEvents(playInfo));
            });

            playEvents = playEvents.concat(barNote.getPlayEvents(playInfo, playEvents));
        });

        player.addEvents(playEvents);
        player.onPlayFinished(() => {
            callback();
        });

        return player;
    };

    public stop = () => {
        this.player.stop();
        this.chordPlayer.stop();
        this.playing = false;
    };

    public toneTransfer = () => {
        console.log('-------音符转调------');
        let transFlag = true;
        const melodyStaves = formatStavesToJson(this.melodyStaveList);
        const melodyStavesCopy = melodyStaves;
        const tone = localStorage.getItem('tone_pitch') as string;
        let initTone = localStorage.getItem('init_tone_pitch') as string;
        if (initTone === null && tone) {
            localStorage.setItem('init_tone_pitch', tone);
            initTone = tone;
        }
        // 大调转化
        if (tone === initTone && tone !== 'C' && tone !== 'none') {
            const interval = distance('C', tone);
            // 是否是降号系列大调
            const isDownTone = tone.indexOf('b') !== -1 || tone === 'F'
            melodyStaves.forEach((melodyNotes, charIndex: number) => {
                const notes = melodyNotes.notes;
                notes.forEach((note, n) => {
                    const key = note.keys[0].split('/')[0];
                    const octave = parseInt(note.keys[0].split('/')[1]);
                    const accidentals = note.accidentals.filter((acc) => acc.index === 0)[0]?.modifier || ''
                    const baseNote = `${key}${accidentals}${octave}`;
                    let template = ['C', 'D', 'E', 'F', 'G', 'A', 'B'].map((note: string) => transpose(note, interval));
                    // 找到升降号出现变化的音
                    template = template.filter(note => note.length === 2);
                    // 上述的音是否影响了当前音
                    let flag = false;
                    let transNote: any = '';
                    template.forEach((note) => {
                        if (note.indexOf(key) !== -1) {
                            flag = true;
                        }
                    })
                    if (flag) {
                        if (accidentals !== '') {
                            // 先还原
                            const noteRestore = `${key}${octave}`;
                            if (!isDownTone) { // 升号大调
                                if (accidentals === 'bb') {
                                    transFlag = false;
                                }
                                if (accidentals === 'n') {
                                    transNote = transpose(noteRestore, `-${interval}`);
                                }
                                if (accidentals === 'b') {
                                    transNote = transpose(`${key}b${octave}`, `-${interval}`);
                                }
                                if (accidentals === '##') {
                                    transNote = transpose(`${key}##${octave}`, `-${interval}`);
                                }
                                if (accidentals === '#') {
                                    transNote = transpose(baseNote, `-${interval}`);
                                }
                            } else { // 降号大调
                                if (accidentals === '##') {
                                    transFlag = false;
                                }
                                if (accidentals === 'n') {
                                    transNote = transpose(noteRestore, `-${interval}`);
                                }
                                if (accidentals === '#') {
                                    transNote = transpose(`${key}#${octave}`, `-${interval}`);
                                }
                                if (accidentals === 'bb') {
                                    transNote = transpose(`${key}bb${octave}`, `-${interval}`);
                                }
                                if (accidentals === 'b') {
                                    transNote = transpose(baseNote, `-${interval}`);
                                }
                            }
                        } else {
                            if (!isDownTone) {
                                transNote = transpose(`${key}#${octave}`, `-${interval}`);
                            } else {
                                transNote = transpose(`${key}b${octave}`, `-${interval}`);
                            }
                        }
                    } else {
                        // 正常转化
                        let note = `${key}${accidentals}${octave}`;
                        if (accidentals === 'n') {
                            note = `${key}${octave}`;
                        }
                        transNote = transpose(note, `-${interval}`);
                    }
                    if (transFlag) {
                        // 更新新的音符
                        transNote = Note.get(Note.simplify(transNote));
                        melodyStavesCopy[charIndex].notes[n].keys = [`${transNote.letter}/${transNote.oct}`];
                        melodyStavesCopy[charIndex].notes[n].accidentals = [{ index: 0, modifier: transNote.acc }];
                        console.log(`【小节:${charIndex};音符顺序:${n}】 ${transNote.letter}${transNote.acc}${transNote.oct}`);
                    }
                })
            })
        } else if (tone !== 'none' && tone !== 'C' && tone !== initTone) {
            Toast.show({
                content: '调号发生临时变化',
            });
        } else if (tone === 'C' || tone === 'none') {
            // 去掉还原号、重生重降等
            melodyStaves.forEach((melodyNotes, charIndex: number) => {
                const notes = melodyNotes.notes;
                notes.forEach((note, n) => {
                    const key = note.keys[0].split('/')[0];
                    const octave = parseInt(note.keys[0].split('/')[1]);
                    const accidentals = note.accidentals.filter((acc) => acc.index === 0)[0]?.modifier || ''
                    const baseNote = `${key}${accidentals}${octave}`;
                    if (accidentals === 'n') {
                        melodyStavesCopy[charIndex].notes[n].keys = [`${key}/${octave}`];
                        melodyStavesCopy[charIndex].notes[n].accidentals = [];
                    }
                    if (accidentals === 'bb' || accidentals === '##') {
                        const noteObj = Note.get(Note.simplify(baseNote));
                        melodyStavesCopy[charIndex].notes[n].keys = [`${noteObj.letter}/${noteObj.oct}`];
                        melodyStavesCopy[charIndex].notes[n].accidentals = [];
                    }
                })
            })
        }
        return { flag: transFlag, melodyTransformed: melodyStavesCopy };
    }

    /**
     * @param melodyNum
     * @param chordNum
     * 升降号处理思路：
     * 1. 首先升降号可以被用户输入，这是升降号的来源
     * 2. 骨架部分先不考虑升降号，一律暂时移除升降号
     * 3. 和弦填充部分考虑升降号，匹配用户输入的音和升降号区间出现的其他升降号
     *    这里要注意的地方：
     *        a. 和弦的升降号不是配出来的，是根据用户的输入影响的
     *        b. 如果一个音加上了升降号后，根据文档中提供的计算音程关系的算法不匹配，需要考虑等音替换，成功匹配就保留，如果也不能成功匹配就丢掉这个和弦
     * 4. 四部和声匹配的时候要考虑和弦带有的升降号
     */
    public structMatching = (melodyNum: number = 1, chordNum: number = 1) => {
        // 音符转化
        const { flag, melodyTransformed } = this.toneTransfer();
        // 获取升降号
        this.findAccNotes(melodyTransformed);
        if (!flag) {
            Toast.show({
                content: '音符输入不符合调式规范！',
            });
            return;
        }
        console.log('-------骨架匹配------');
        this.melodyNum = melodyNum;
        this.chordNum = chordNum;
        // 如果非选中某一个小节或者第一次渲染和弦，就把对应关系付给全局变量
        if (this.currentBarIndex === undefined) {
            Array.from({ length: 1000 }, (_, index) => index).forEach((val: number) => {
                this.melodyNumber[val] = melodyNum;
                this.chordNumber[val] = chordNum;
            });
        } else {
            const index = Math.floor(this.currentBarIndex);
            // 否则就把每次选中的对应关系保存到相关的小节中
            this.melodyNumber[index] = melodyNum;
            this.chordNumber[index] = chordNum;
        }
        // 先拿到当前的旋律部分音符
        // const melodyStaves = formatStavesToJson(this.melodyStaveList);
        const melodyStaves = melodyTransformed;
        let chordStructs: Array<any> = [];
        let baseNotes: Array<any> = [];
        const chordDurations: Array<any> = [];
        const melodyDurations: Array<any> = [];
        melodyStaves.forEach((stave, i) => {
            // i是第几小节
            chordStructs[i] = [];
            baseNotes[i] = [];
            const notes = stave.notes;
            const currentBarMelodyNum = this.melodyNumber[i] !== undefined ? this.melodyNumber[i] : melodyNum;
            notes.forEach((note, n) => {
                // 当前音符
                const key = note.keys[0].split('/')[0];
                const octave = parseInt(note.keys[0].split('/')[1]);
                const accidentals = note.accidentals.filter((acc) => acc.index === 0)[0]?.modifier || '';
                const baseDuration = note.duration;
                let chordDuration = '4';
                const baseNote = `${key}${accidentals}${octave}`;
                // 根据旋律和和弦的对应关系，找到需要搭配骨架的旋律音
                if (currentBarMelodyNum === 1) {
                    chordDuration = baseDuration;
                }
                if (currentBarMelodyNum === 2) {
                    if (baseDuration === '1') {
                        chordDuration = '1';
                    }
                    if (baseDuration === '2') {
                        chordDuration = '1';
                    }
                    if (baseDuration === '4') {
                        chordDuration = '2';
                    }
                    // 二对一
                    if (baseDuration === '2' && n === 1) {
                        // 二分音符只给第一个配和弦
                        return;
                    }
                    if (baseDuration === '4' && (n === 1 || n === 3)) {
                        // 四分音符只给第一个和第三个配和弦
                        return;
                    }
                }
                if (currentBarMelodyNum === 4) {
                    chordDuration = '1';
                    // 四对一
                    if (baseDuration === '2' && n === 1) {
                        // 二分音符只给第一个配和弦
                        return;
                    }
                    if (baseDuration === '4' && n !== 0) {
                        // 四分音符只给第一个配和弦
                        return;
                    }
                }
                baseNotes[i].push(baseNote);
                // 骨架和旋律音的时长是一一对应的
                chordDurations.push(chordDuration);
                melodyDurations.push(baseDuration);
                // 上一个音符
                let previousMelody: any;
                let previousChord: any;
                // 上上一个音符
                let precedingChord: any;
                // 生成的关系
                let relation: any;
                let _note: any;
                if (i === 0 && n === 0) {
                    // 第一个旋律的音程是纯八、五度、大三或者小三 (暂时先不考虑小三)
                    const _rst = generate358IntervalNote(baseNote);
                    _note = _rst._note;
                    relation = _rst.relation;
                    // 四部和声要求 - 调整音程
                    _note = this.adjustChord(baseNote, _note);
                    chordStructs[i].push(_note);
                    console.log(`【小节:0;音符顺序:0】 按照音程关系${relation}更新二声部骨架${_note}`);
                    return;
                }
                // 找到上一个和上上一个音符
                let currentNoteIndex = n;
                let currentBarIndex = i;
                let _rst: any = findPreviousTwoNote(currentNoteIndex, currentBarIndex, melodyStaves, chordStructs, currentBarMelodyNum);
                previousMelody = _rst.previousMelody;
                previousChord = _rst.previousChord;
                precedingChord = _rst.precedingChord;

                // 先根据小节数和拍数过滤一下当前可用的accNote
                const accNoteFiltered = this.accNotes.filter(note => {
                    return note.barIndex === i && n >= note.noteIndex
                });
                // 优先级进，并尽量适配3、6度音程
                _rst = generateStepNote({
                    section_index: i,
                    note_index: n
                }, previousMelody, previousChord, baseNote, 'allow_same_note', accNoteFiltered);
                _note = _rst._note;

                // 判断平行3/6度，同音反复
                const parallelAllow = checkParallel(currentNoteIndex, currentBarIndex, melodyStaves, chordStructs, baseNote, _note);

                if (!parallelAllow) {
                    // 平行三六度、同音反复检测失败，插入5/8度音程
                    _rst = generate58IntervalNote({ section_index: i, note_index: n }, baseNote);
                    _note = _rst._note;
                }
                // 判断是否大跳
                // _rst = handleJumping({section_index: i, note_index: n}, previousChord, precedingChord, baseNote, _note);
                // _note = _rst._note;

                if (previousMelody && previousChord) {
                    // 避免平行八度：
                    _rst = handleParallelOctave({
                        section_index: i,
                        note_index: n
                    }, previousMelody, previousChord, baseNote, _note);
                    _note = _rst._note;
                    // 避免平行五度：
                    _rst = handleParallelFifth({
                        section_index: i,
                        note_index: n
                    }, previousMelody, previousChord, baseNote, _note);
                    _note = _rst._note;
                    // 避免隐伏五八度
                    _rst = handleHiddenOctaveFifth({
                        section_index: i,
                        note_index: n
                    }, previousMelody, previousChord, baseNote, _note);
                    _note = _rst._note;
                }

                // 更新最终的note
                _note = this.adjustChord(baseNote, _note);
                chordStructs[i].push(_note);
            });
        });

        // 转化一层
        let _i = 0;
        const rst: Array<any> = [];
        chordStructs.forEach((notes: Array<string>, charIndex: number) => {
            rst[charIndex] = [];
            notes.forEach((note: string, index: number) => {
                // 计算音程的时候也要考虑升降号
                let noteTransformed = note;
                this.accNotes.forEach(accNote => {
                    if (charIndex === accNote.barIndex && index >= accNote.noteIndex) {
                        const noteObj = Note.get(note);
                        if (!noteObj.acc && noteObj.letter === accNote.letter) {
                            noteTransformed = `${accNote.letter}${accNote.acc}${noteObj.oct}`;
                        }
                    }
                })
                const relation = simplifyRelation(noteTransformed, baseNotes[charIndex][index]);
                // 去除升降号(简单粗暴的方式)
                note = Note.simplify(note);
                if (note.length === 3) {
                    note = `${note[0]}${note[2]}`;
                }
                rst[charIndex].push({
                    note,
                    relation,
                    chordDuration: chordDurations[_i],
                    melodyDuration: melodyDurations[_i],
                    baseNote: baseNotes[charIndex][index],
                });
                _i++;
            });
        });

        // 渲染界面
        // const chordRst = staveTransferForStruct(rst);
        // const scoreString = localStorage.getItem(SCORE_STORE_KEY);
        // const score = scoreString ? JSON.parse(scoreString) : undefined;
        // score.chordStaves = chordRst;
        // this.restoreScore(score);
        // this.redraw();

        // 获取离调信息
        this.detuningInfo = this.findDetuningInfo(rst);
        console.log(`【骨架构成】`, rst)
        this.chordMatching(rst);
    };

    public chordMatching = (chordRst: Array<chordNoteBarType>) => {
        console.log('-------和弦构成------');
        this.test();
        let chordStaves: Array<chordMatchingBarType> = [];
        chordRst.forEach((chords, charIndex) => {
            chordStaves[charIndex] = [];
            chords.forEach((chord: chordNoteType, n: number) => {
                chordStaves[charIndex][n] = {};
                // 旋律音
                const baseNote = chord.baseNote;
                // 根音
                let rootNote = chord.note;
                let secondNote = '';
                let thirdNote = '';
                let chordNotes: string[][] = [];
                // 是否离调
                const detuningMatch = this.detuningInfo.filter((item) => item.barIndex === charIndex && item.noteIndex === n);

                // 与旋律部分间隔八度
                if (['8P', '-8P', '1P', '-1P'].indexOf(chord.relation) !== -1) {
                    // 原始和弦
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '5P');
                    chordNotes.push([secondNote, thirdNote, '原位']);
                    // 第一转位
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '6M');
                    chordNotes.push([secondNote, thirdNote, '第一转位']);
                    // 第二转位
                    secondNote = transpose(rootNote, '4P');
                    thirdNote = transpose(rootNote, '6M');
                    chordNotes.push([secondNote, thirdNote, '第二转位']);
                    console.log(`【小节:${charIndex};音符顺序:${n}】 旋律: ${baseNote} 骨架: ${rootNote}, 原始和弦、第一转位和第二转位`);
                }

                // 与旋律部分间隔三度
                if (['3M', '-3M', '3m', '-3m'].indexOf(chord.relation) !== -1) {
                    // 原始和弦
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '5P');
                    chordNotes.push([secondNote, thirdNote, '原位']);
                    // 第一转位
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '6M');
                    chordNotes.push([secondNote, thirdNote, '第一转位']);
                    console.log(`【小节:${charIndex};音符顺序:${n}】 旋律: ${baseNote} 骨架: ${rootNote}, 原始和弦和第一转位`);
                }

                // 与旋律部分间隔六度
                if (['6M', '-6M', '6m', '-6m'].indexOf(chord.relation) !== -1) {
                    // 第一转位
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '6M');
                    chordNotes.push([secondNote, thirdNote, '第一转位']);
                    // 第二转位
                    secondNote = transpose(rootNote, '4P');
                    thirdNote = transpose(rootNote, '6M');
                    chordNotes.push([secondNote, thirdNote, '第二转位']);
                    console.log(`【小节:${charIndex};音符顺序:${n}】 旋律: ${baseNote} 骨架: ${rootNote}, 第一转位和第二转位`);
                }

                // 与旋律部分间隔五度
                if (['5P', '-5P', '5d', '-5d'].indexOf(chord.relation) !== -1) {
                    // 仅有原位
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '5P');
                    chordNotes.push([secondNote, thirdNote, '原位']);
                    console.log(`【小节:${charIndex};音符顺序:${n}】 旋律: ${baseNote} 骨架: ${rootNote}, 原位`);
                }

                // 与旋律部分间隔四度
                if (['4P', '-4P'].indexOf(chord.relation) !== -1) {
                    // 仅有第二转位
                    secondNote = transpose(rootNote, '4P');
                    thirdNote = transpose(rootNote, '6M');
                    chordNotes.push([secondNote, thirdNote, '第二转位']);
                    console.log(`【小节:${charIndex};音符顺序:${n}】 旋律: ${baseNote} 骨架: ${rootNote}, 第二转位`);
                }

                if (chordNotes.length === 0) {
                    secondNote = transpose(rootNote, '3M');
                    thirdNote = transpose(rootNote, '5P');
                    chordNotes.push([secondNote, thirdNote, '原位']);
                    console.log(`【小节:${charIndex};音符顺序:${n}】 旋律: ${baseNote} 骨架: ${rootNote}, 大三度和纯五度`);
                }

                // 对比 C 大调和弦
                chordNotes = chordNotes.reduce((accumulator: string[][], current: string[]) => {
                    const noteType = current[2];
                    const secondNote: any = current[0].length >= 4 ? Note.simplify(current[0]) : current[0];
                    const thirdNote: any = current[1].length >= 4 ? Note.simplify(current[1]) : current[1];

                    const eFBCMatch = ['E#', 'Fb', 'B#', 'Cb'].some(val => [secondNote, thirdNote].indexOf(val) !== -1)

                    if (rootNote.length === 3 || secondNote.length === 3 || thirdNote.length === 3 || eFBCMatch) {
                        if (chordDetect(rootNote[0], secondNote[0], thirdNote[0])) {
                            accumulator.push(current)
                            console.log(`【小节:${charIndex};音符顺序:${n}】 匹配C大调和弦 - 0，原和弦 ${rootNote},${secondNote},${thirdNote}`)
                        } else {
                            // 看能否同音转换匹配 C 大调
                            if (chordDetect(rootNote[0], Note.enharmonic(secondNote)[0], thirdNote[0])) {
                                accumulator.push([
                                    Note.enharmonic(secondNote),
                                    thirdNote,
                                    noteType
                                ])
                                console.log(`【小节:${charIndex};音符顺序:${n}】 匹配C大调和弦 - 2，原和弦 ${rootNote},${secondNote},${thirdNote}，转化为${rootNote},${Note.enharmonic(secondNote)},${thirdNote}`)
                            } else if (chordDetect(rootNote[0], secondNote[0], Note.enharmonic(thirdNote)[0])) {
                                accumulator.push([
                                    secondNote,
                                    Note.enharmonic(thirdNote),
                                    noteType
                                ])
                                console.log(`【小节:${charIndex};音符顺序:${n}】 匹配C大调和弦 - 3，原和弦 ${rootNote},${secondNote},${thirdNote}，转化为${rootNote},${secondNote},${Note.enharmonic(thirdNote)}`)
                            } else if (chordDetect(rootNote[0], Note.enharmonic(secondNote)[0], Note.enharmonic(thirdNote)[0])) {
                                accumulator.push([
                                    Note.enharmonic(secondNote),
                                    Note.enharmonic(thirdNote),
                                    noteType
                                ])
                                console.log(`【小节:${charIndex};音符顺序:${n}】 匹配C大调和弦 - 23，原和弦 ${rootNote},${secondNote},${thirdNote}，转化为${rootNote},${Note.enharmonic(secondNote)},${Note.enharmonic(thirdNote)}`)
                            }
                        }
                    } else {
                        accumulator.push(current)
                    }

                    return accumulator
                }, [])

                chordNotes = chordNotes.map(chordNote => {
                    let secondNote = chordNote[0].length >= 4 ? Note.simplify(chordNote[0]) : chordNote[0];
                    let thirdNote = chordNote[1].length >= 4 ? Note.simplify(chordNote[1]) : chordNote[1];
                    const chordType = chordNote[2];

                    // 这里先去除升降号，统一在填充和弦的最后一步处理
                    if (secondNote.length === 3) {
                        secondNote = `${secondNote[0]}${secondNote[2]}`;
                    }
                    if (thirdNote.length === 3) {
                        thirdNote = `${thirdNote[0]}${thirdNote[2]}`;
                    }

                    return [secondNote, thirdNote, chordType]
                });

                chordNotes = chordNotes.map((chordNote) => {
                    // 判断声部超越的问题
                    let secondNote = chordNote[0];
                    let thirdNote = chordNote[1];
                    const chordType = chordNote[2];

                    // 处理声部超越的问题
                    let octInterval = 0;
                    const thirdNoteDistance = Interval.get(distance(thirdNote, baseNote));
                    if (thirdNoteDistance.dir === -1) {
                        // 发生了声部超越的问题
                        const semitones = thirdNoteDistance.semitones || 0;
                        if ((Note.get(chord.note)?.oct || 0) >= 4) {
                            octInterval = Math.floor(Math.abs(semitones) / 12) + 1;
                        } else {
                            octInterval = 1;
                        }
                    } else if (thirdNoteDistance.semitones === 0) {
                        // 同音
                        octInterval = 1;
                    }

                    if (octInterval > 0) {
                        console.log(
                            `【小节:${charIndex};音符顺序:${n}】 避免声部超越，向下平移${octInterval}个八八度，原始根音: ${chord.note} 构成音1: ${secondNote} 构成音2: ${thirdNote}`,
                        );
                        // 整体向下移动
                        rootNote = Note.transpose(chord.note, `-${octInterval * 8}P`);
                        secondNote = Note.transpose(secondNote, `-${octInterval * 8}P`);
                        thirdNote = Note.transpose(thirdNote, `-${octInterval * 8}P`);
                    }
                    console.log(`【小节:${charIndex};音符顺序:${n}】 匹配和弦构成: 根音: ${rootNote} 构成音1: ${secondNote} 构成音2: ${thirdNote}`);
                    return [rootNote, secondNote, thirdNote, chordType];
                });
                // 前面的三和弦一定要配，七和弦看情况要不要配
                if (detuningMatch.length > 0) {
                    const detuningInfo = detuningMatch[0];
                    // 配大小七和弦，重新编配骨架音，找到和上一个骨架音音程最小的小七度
                    const previousStruct = this.findPreviousItem(chordRst, charIndex, n);
                    // 根据离调是上行还是下行找到对应的七和弦
                    let seventhNotes = [];
                    if (detuningInfo.direction === 'up') {
                        seventhNotes = findUpDirectionSeventhNotes(detuningInfo.fromNote)
                    } else {
                        seventhNotes = findDownDirectionSeventhNotes(detuningInfo.fromNote)
                    }
                    const processed = seventhNoteProcessing(seventhNotes, previousStruct.note, this.accNotes)
                    this.seventhNotes[`${charIndex}_${n}`] = processed
                    this.chordSolveHelper = this.generateChordSolveHelper(processed, detuningInfo.direction);
                }

                // 根据音区和用户输入的旋律加入升降号
                chordNotes = chordNotes.map((chordNote) => {
                    let rootNote = chordNote[0];
                    let secondNote = chordNote[1];
                    let thirdNote = chordNote[2];
                    const chordType = chordNote[3];
                    if (baseNote.length === 3) {
                        const acc = baseNote[1];
                        if (rootNote[0] === baseNote[0]) {
                            rootNote = `${rootNote[0]}${acc}${rootNote[1]}`;
                        }
                        if (secondNote[0] === baseNote[0]) {
                            secondNote = `${secondNote[0]}${acc}${secondNote[1]}`;
                        }
                        if (thirdNote[0] === baseNote[0]) {
                            thirdNote = `${thirdNote[0]}${acc}${thirdNote[1]}`;
                        }
                    }
                    // 按照音区, 等音转换
                    // rootNote = noteEnharmonicByAcc(rootNote, this.accNotes, charIndex, n);
                    // secondNote = noteEnharmonicByAcc(secondNote, this.accNotes, charIndex, n)
                    // thirdNote = noteEnharmonicByAcc(thirdNote, this.accNotes, charIndex, n)

                    return [rootNote, secondNote, thirdNote, chordType];
                });

                chordStaves[charIndex][n] = {
                    chordNotes: chordNotes,
                    relation: chord.relation,
                    duration: chord.chordDuration,
                };
            });
        });

        // console.log(chordStaves);
        //
        // const scoreString = localStorage.getItem(SCORE_STORE_KEY);
        // const score = scoreString ? JSON.parse(scoreString) : undefined;
        // score.chordStaves = staveTransferForChordMatching(chordStaves);
        // this.restoreScore(score);
        // this.redraw();

        this.vocalStructMatching(chordRst, chordStaves);
    };

    // 四部和声
    public vocalStructMatching = (structRst: Array<chordNoteBarType>, chordStaves: Array<chordMatchingBarType>) => {
        console.log('-------进行四部和声匹配------');
        let chordStave: Array<any> = [];
        let melodyStave: Array<any> = [];
        let previousAlto = '';
        let previousTenor = '';
        let previousBass = '';
        let userInput: any = [];
        // 正常匹配四部和声
        structRst.map((structChar: any, charIndex: number) => {
            // 先把高音谱区需要渲染的部分确定一部分（有些需要填充四部和声，有些不需要）
            melodyStave[charIndex] = this.matchMelody(charIndex);
            userInput[charIndex] = this.matchMelody(charIndex);
            chordStave[charIndex] = [];
            structChar.forEach((structInfo: any, n: number) => {
                // 判断骨架音程
                const soprano = structInfo.baseNote; // 高声部
                const chordNotes = (chordStaves[charIndex][n] as any)['chordNotes']; // 三个和弦音(和和弦类型)
                const rst = this.findHarmonyMatches(soprano, chordNotes, charIndex, n);
                if (rst.length === 0) {
                    const bass = chordNotes[0][0];
                    // 人工匹配, 紧凑排列
                    let t = Note.get(transpose(soprano, '-3M'));
                    let alto = '';
                    let tenor = '';
                    if (!t.empty) {
                        alto = t.letter + t.oct;
                    }
                    t = Note.get(transpose(soprano, '-5P'));
                    if (!t.empty) {
                        tenor = t.letter + t.oct;
                    }
                    rst.push({ alto, tenor, bass, type: '人工匹配', chordType: '原位' });
                }
                // 按照音符时值，更新高音谱区音符
                const melodyDuration = structInfo.melodyDuration;
                const chordDuration = structInfo.chordDuration;

                const { staveIndex, changeStave } = this.matchRequired(melodyDuration, chordDuration, n);

                // 匹配四部和声
                let alto = '';
                let tenor = '';
                let bass = '';
                let type = '';
                let chordType = '';
                if (rst.length === 1) {
                    ({ alto, tenor, bass, type, chordType } = rst[0]); // 人工匹配
                    previousAlto = alto;
                    previousTenor = tenor;
                    previousBass = bass;
                } else if (charIndex === 0 && n === 0) {
                    // 第一小节, 随机拿出一组
                    ({ alto, tenor, bass, type, chordType } = rst[Math.floor(Math.random() * rst.length)]);
                    previousAlto = alto;
                    previousTenor = tenor;
                    previousBass = bass;
                } else {
                    const semitoneRst = this.vocalSort(rst, previousAlto, previousTenor);

                    // 确定当前四部和声
                    ({ alto, tenor, bass, type, chordType } = semitoneRst[0]);

                    // 如果低音和次中音和上一个低声部相差一个8度，就把它往上移一个8度
                    const bassTenorSemiton = Math.abs(Interval.get(distance(tenor, bass)).semitones || 0);
                    const bassToBassSemiton = Math.abs(Interval.get(distance(previousBass, bass)).semitones || 0);

                    if (bassTenorSemiton > 12 || bassToBassSemiton > 12) {
                        console.log(`【小节:${charIndex};音符顺序:${n}】上移8度: 原始低音:${bass}`);
                        bass = transpose(bass, '8P');
                    }

                    previousAlto = alto;
                    previousTenor = tenor;
                    previousBass = bass;
                }
                console.log(
                    `【小节:${charIndex};音符顺序:${n}】${type} - ${chordType} 匹配四部和声 - 高音：${soprano}, 中音:${alto}, 次中音:${tenor}, 低音:${bass}`,
                );

                if (changeStave) {
                    const durations = this.matchDurations(melodyDuration);

                    melodyStave[charIndex][staveIndex] = {
                        notes: [soprano, alto],
                        duration: melodyDuration,
                        durations,
                    };
                }
                // 更新低音谱区音符
                chordStave[charIndex].push({
                    notes: [tenor, bass],
                    duration: structInfo.chordDuration,
                });
            });
        });

        let triggerDetuning = false;
        if (this.detuningInfo.length !== 0 && this.melodyNum === 1 && this.chordNum === 1 && this.accNotes.length === 1) {
            triggerDetuning = true;
            // 七和弦匹配出来的四部和声
            let seventhVocal: any = {}
            // 七和弦解决出来的和弦
            let seventhSolveRst: any = {}
            // 全部四部和声
            let vocalRst: any = {}
            // 先确定离调部分的四部和声
            structRst.map((structChar: any, charIndex: number) => {
                structChar.forEach((structInfo: any, n: number) => {
                    const soprano = structInfo.baseNote; // 高声部
                    // 遍历直到进入了第一个离调区间
                    const detuningMatch = this.detuningInfo.filter((item) => item.barIndex === charIndex && item.noteIndex === n);
                    if (detuningMatch.length > 0) {
                        // 此处的七和弦
                        const currentSeventhNote = this.seventhNotes[`${charIndex}_${n}`];
                        // 把这里的七和弦都按照四部和声的方式排列出来
                        let matches: any[] = [];
                        currentSeventhNote.forEach((notes: string[]) => {
                            matches = matches.concat(this.findHarmonies(soprano, notes[2], notes[3], charIndex, n));
                        })
                        matches = matches.filter(item => {
                            // 满足四部和声条件的音符和七和弦的一一对应
                            const seventhNote = currentSeventhNote.filter((note: string[]) => note[3] === item.chordType)[0];
                            if (seventhNote) {
                                const alto = seventhNote[0]; // 七和弦对应四部和声的中音
                                const tenor = seventhNote[1]; // 七和弦对应四部和声的次中音

                                return (this.noteIsEqual(item.alto, alto) && this.noteIsEqual(item.tenor, tenor)) ||
                                    (this.noteIsEqual(item.alto, tenor) && this.noteIsEqual(item.tenor, alto))
                            }
                            return false;
                        });
                        if (matches.length === 0) {
                            // 七和弦四部和声匹配失败
                            triggerDetuning = false;
                        } else {
                            seventhVocal[`${charIndex}_${n}`] = matches;
                        }
                    }
                });
            });
            // 后解决三和弦
            structRst.map((structChar: any, charIndex: number) => {
                structChar.forEach((structInfo: any, n: number) => {
                    const seventhVocalMatch = seventhVocal[`${charIndex}_${n}`];
                    if (seventhVocalMatch && seventhVocalMatch.length > 0) {
                        // 七和弦的解决 (已做了转化和音区过滤
                        seventhSolveRst[`${charIndex}_${n}`] = this.seventhNoteSolve(structInfo.baseNote, seventhVocalMatch, charIndex, n);
                        // 解决过后把无法解决的七和弦过滤掉
                        seventhVocal[`${charIndex}_${n}`] = seventhVocal[`${charIndex}_${n}`].filter((val: any) => {
                            let t = false;
                            Object.keys(seventhSolveRst[`${charIndex}_${n}`]).forEach(key => {
                                if (key.indexOf(val.chordType) !== -1) {
                                    seventhSolveRst[`${charIndex}_${n}`][key].forEach((solve: string[]) => {
                                        const originalAlto = solve[3]
                                        const originalTenor = solve[4]
                                        const originalBass = solve[5]
                                        if (val.alto === originalAlto && val.tenor === originalTenor && val.bass === originalBass) {
                                            t = true
                                        }
                                    })
                                }
                            })
                            return t;
                        })
                        if (seventhVocal[`${charIndex}_${n}`].length === 0) {
                            triggerDetuning = false;
                        }
                    }
                });
            });

            // 向前或者向后匹配普通音符的四部和声
            let count = 0;
            if (triggerDetuning) {
                const firstDetuningInfo = this.detuningInfo.filter((c) => c.detuningStart)[0];
                const firstDetuningBarIndex = firstDetuningInfo.barIndex
                const firstDetuningNoteIndex = firstDetuningInfo.noteIndex
                structRst.map((structChar: any, charIndex: number) => {
                    structChar.forEach((structInfo: any, n: number) => {
                        count += 1;
                        let previousAlto = '';
                        let previousTenor = '';
                        let alto = '';
                        let tenor = '';
                        let bass = '';
                        let type = ''; // ie: 混合排列（前6后9）
                        let chordType = ''; // ie: 减七Ⅰ三转位

                        if (seventhVocal[`${charIndex}_${n}`]) {
                            // 如果当前位置是七和弦
                            const currentSeventhNotes = seventhVocal[`${charIndex}_${n}`];
                            const randomIndex = Math.floor(Math.random() * currentSeventhNotes.length);
                            const currentVocal = currentSeventhNotes[randomIndex];
                            // 固定这个位置的七和弦
                            seventhVocal[`${charIndex}_${n}`] = currentVocal;
                            vocalRst[`${charIndex}_${n}`] = currentVocal;

                            if (charIndex === firstDetuningBarIndex && n === firstDetuningNoteIndex) {
                                // 如果也是第一次离调
                                previousAlto = currentVocal.alto;
                                previousTenor = currentVocal.tenor;

                                // 第一次离调，向前匹配四部和声
                                let previousCharIndex: number = charIndex;
                                let previousNoteIndex: number = n;
                                for (let i = 1; i < count; i++) {
                                    const rst = findPreviousIndexes(previousCharIndex, previousNoteIndex);
                                    previousCharIndex = rst.previousCharIndex;
                                    previousNoteIndex = rst.previousNoteIndex;
                                    if (previousCharIndex !== -1 && previousNoteIndex !== -1) {
                                        // 常规三和弦
                                        const chordNotes = (chordStaves[previousCharIndex][previousNoteIndex] as any)['chordNotes'];
                                        // 常规三和弦的四部和声们
                                        const rst = this.findHarmonyMatches(structRst[previousCharIndex][previousNoteIndex].baseNote, chordNotes, previousCharIndex, previousNoteIndex);

                                        const semitoneRst = this.vocalSort(rst, previousAlto, previousTenor);

                                        // 确定当前四部和声
                                        ({ alto, tenor, bass, type, chordType } = semitoneRst[0]);

                                        previousAlto = alto;
                                        previousTenor = tenor;

                                        vocalRst[`${previousCharIndex}_${previousNoteIndex}`] = {
                                            alto,
                                            tenor,
                                            bass,
                                            type,
                                            chordType
                                        };
                                    }
                                }
                            }
                        }

                        if (charIndex > firstDetuningBarIndex ||
                            (charIndex === firstDetuningBarIndex && n > firstDetuningNoteIndex)) {
                            // 向后匹配四部和声
                            // 看上一个音符是否是离调，判断当前是否能被解决
                            const previousIndexes = findPreviousIndexes(charIndex, n);
                            const preCharIndex = previousIndexes.previousCharIndex;
                            const preNoteIndex = previousIndexes.previousNoteIndex;
                            const detuningInfo = this.detuningInfo.filter(c => c.barIndex === preCharIndex && c.noteIndex === preNoteIndex)
                            if (detuningInfo.length > 0) {
                                // 可以被解决
                                const seventhTypeShuffle = shuffleArray(['_大三', '_小三', ''])
                                // 前面的七和弦
                                const preSeventhVocal = vocalRst[`${preCharIndex}_${preNoteIndex}`];
                                seventhTypeShuffle.forEach(t => {
                                    const v = seventhSolveRst[`${preCharIndex}_${preNoteIndex}`][`${preSeventhVocal.chordType}${t}`]
                                    if (v) {
                                        vocalRst[`${charIndex}_${n}`] = { solve: v[0] }
                                        previousAlto = v[0][0]
                                        previousTenor = v[0][1]
                                    }
                                })
                            } else {
                                // 常规三和弦匹配
                                const soprano = structInfo.baseNote;
                                const chordNotes = (chordStaves[charIndex][n] as any)['chordNotes']
                                const rst = this.findHarmonyMatches(soprano, chordNotes, charIndex, n);
                                const semitoneRst = this.vocalSort(rst, previousAlto, previousTenor);
                                // 确定当前四部和声
                                ({ alto, tenor, bass, type, chordType } = semitoneRst[0]);

                                vocalRst[`${charIndex}_${n}`] = { alto, tenor, bass, type, chordType };

                                previousAlto = alto;
                                previousTenor = tenor;
                            }
                        }
                    });
                })
            }
            // console.log(vocalRst)
            // console.log(chordStave)
            // console.log(melodyStave)

            // 渲染离调和弦
            if (triggerDetuning) {
                console.log('-------离调七和弦四部和声匹配------');
                const newMelodyStave: any = [];
                const newChordStave: any = [];
                structRst.forEach((structChar: any, charIndex: number) => {
                    structChar.forEach((structInfo: any, n: number) => {
                        const rstItem = vocalRst[`${charIndex}_${n}`];
                        if (!newChordStave[charIndex] || !newMelodyStave[charIndex]) {
                            newChordStave[charIndex] = []
                            newMelodyStave[charIndex] = []
                        }
                        if (rstItem.chordType) {
                            const type = rstItem.type;
                            const chordType = rstItem.chordType;
                            const soprano = structInfo.baseNote;
                            const alto = rstItem.alto;
                            const tenor = rstItem.tenor;
                            const bass = rstItem.bass;
                            newMelodyStave[charIndex].push({ duration: '4', notes: [soprano, alto] });
                            newChordStave[charIndex].push({ duration: '4', notes: [tenor, bass] });

                            if (['原位', '第一转位', '第二转位'].indexOf(rstItem.chordType) !== -1) {
                                console.log(`【小节:${charIndex};音符顺序:${n}】${type} - ${chordType} 匹配四部和声 - 高音：${soprano}, 中音:${alto}, 次中音:${tenor}, 低音:${bass}`);
                            } else {
                                console.log(`【小节:${charIndex};音符顺序:${n}】七和弦 - ${type} - ${chordType} 匹配四部和声 - 高音：${soprano}, 中音:${alto}, 次中音:${tenor}, 低音:${bass}`);
                            }

                        } else if (rstItem.solve) {
                            const i = rstItem.solve
                            const soprano = structInfo.baseNote;
                            const alto = i[0];
                            const tenor = i[1];
                            const bass = i[2];
                            const fromAlto = i[3];
                            const fromTenor = i[4];
                            const fromBass = i[5];
                            newMelodyStave[charIndex].push({ duration: '4', notes: [soprano, alto] });
                            newChordStave[charIndex].push({ duration: '4', notes: [tenor, bass] });
                            console.log(`【小节:${charIndex};音符顺序:${n}】七和弦解决 匹配四部和声 - 高音：${soprano}, 中音:${alto}, 次中音:${tenor}, 低音:${bass}`);
                            console.log(`【小节:${charIndex};音符顺序:${n}】七和弦解决 原四部和声 - 中音:${fromAlto}(${i[6]}), 次中音:${fromTenor}(${i[7]}), 低音:${fromBass}(${i[8]})`)
                        }
                    });
                });
                chordStave = newChordStave;
                melodyStave = newMelodyStave;
            }
        }
        // 转化回到原调
        const tone = localStorage.getItem('tone_pitch') as string;
        if (tone && tone !== 'C' && tone !== 'none') {
            const interval = distance('C', tone);
            let template = ['C', 'D', 'E', 'F', 'G', 'A', 'B'].map((note: string) => transpose(note, interval));
            // 找到升降号出现变化的音
            template = template.filter(note => note.length === 2);
            console.log('-------低音区转化回原调------');
            chordStave.forEach((notes, charIndex: number) => {
                notes.forEach((note: any, n: number) => {
                    note.notes = note.notes.map((v: string) => {
                        const noteObj = Note.get(Note.simplify(transpose(v, interval)));
                        if (template.indexOf(noteObj.letter + noteObj.acc) !== -1) {
                            const noteTransformed = (noteObj.letter as string) + noteObj.oct;
                            console.log(`【小节:${charIndex};音符顺序:${n}】${v} 转化为 ${noteTransformed}`);
                            return (noteObj.letter as string) + noteObj.oct
                        }
                        console.log(`【小节:${charIndex};音符顺序:${n}】${v} 转化为 ${Note.simplify(transpose(v, interval))}`);
                        return Note.simplify(transpose(v, interval))
                    })
                });
            })
            console.log('-------高音区转化回原调------');
            melodyStave.forEach((notes, charIndex) => {
                notes.forEach((note: any, n: number) => {
                    note.notes = note.notes.map((v: string, i: number) => {
                        if (i > 0) {
                            const noteObj = Note.get(Note.simplify(transpose(v, interval)));
                            if (template.indexOf(noteObj.letter + noteObj.acc) !== -1) {
                                // 包含升级号的转化
                                const noteTransformed = (noteObj.letter as string) + noteObj.oct;
                                console.log(`【小节:${charIndex};音符顺序:${n}】${v} 转化为 ${noteTransformed}`);
                                return (noteObj.letter as string) + noteObj.oct
                            }

                            console.log(`【小节:${charIndex};音符顺序:${n}】${v} 转化为 ${Note.simplify(transpose(v, interval))}`);
                            // 不包含升降号
                            return Note.simplify(transpose(v, interval))
                        }
                    })
                });
            })
        }
        // 用户输入行保持一致
        melodyStave.forEach((notes, charIndex) => {
            notes.forEach((note: any, n: number) => {
                note.notes = note.notes.map((v: string, i: number) => {
                    if (i === 0) {
                        return userInput[charIndex][n].notes[0];
                    }
                    return v;
                })
            });
        })

        chordStave = staveDataTransfer(chordStave, 'bass_to_clef');
        melodyStave = staveDataTransfer(melodyStave, false);
        const scoreString = localStorage.getItem(SCORE_STORE_KEY);
        const score = scoreString ? JSON.parse(scoreString) : undefined;
        chordStave = addDirection(chordStave)
        melodyStave = addDirection(melodyStave)
        score.chordStaves = chordStave;
        score.melodyStaves = melodyStave;
        this.restoreScore(score);
        this.redraw();
    };


    private seventhNoteSolve = (
        userInputNote: string,
        seventhVocalMatch: Array<{ alto: string, tenor: string, bass: string, chordType: string }>,
        charIndex: number,
        n: number
    ) => {
        /*
        * 数字对应音程
        * 0: 1P
        * 1: 2m
        * 2: 2M
        * 5: 4P
        * 7: 5P
        * */
        const transferEffective = (note: string, intervalName: string) => {
            let noteTransformed = transpose(note, intervalName);
            noteTransformed = noteEnharmonicByAcc(noteTransformed, this.accNotes, charIndex, n);
            return noteMatchAcc(noteTransformed, this.accNotes) ? noteTransformed : false;
        }
        const rst: any = {}
        seventhVocalMatch.forEach(i => {
            const note1 = Note.pitchClass(i.alto)
            const note2 = Note.pitchClass(i.tenor)
            const note3 = Note.pitchClass(i.bass)
            if (this.chordSolveHelper[i.chordType + '_大三']) {
                rst[i.chordType + '_大三'] = []
                const t1 = this.chordSolveHelper[i.chordType + '_大三'][note1];
                const t2 = this.chordSolveHelper[i.chordType + '_大三'][note2];
                const t3 = this.chordSolveHelper[i.chordType + '_大三'][note3];
                if (t1 && t2 && t3) {
                    // 解决到大三
                    t1.forEach((alto_t: string) => {
                        t2.forEach((tenor_t: string) => {
                            t3.forEach((bass_t: string) => {
                                const altoEffective = transferEffective(i.alto, alto_t)
                                const tenorEffective = transferEffective(i.tenor, tenor_t)
                                const bassEffective = transferEffective(i.bass, bass_t)
                                if (altoEffective && tenorEffective && bassEffective) {
                                    rst[i.chordType + '_大三'].push([
                                        altoEffective,
                                        tenorEffective,
                                        bassEffective,
                                        i.alto, i.tenor, i.bass,
                                        alto_t, tenor_t, bass_t
                                    ])
                                }
                            })
                        })
                    })
                }
            }
            if (this.chordSolveHelper[i.chordType + '_小三']) {
                rst[i.chordType + '_小三'] = []
                const t4 = this.chordSolveHelper[i.chordType + '_小三'][note1];
                const t5 = this.chordSolveHelper[i.chordType + '_小三'][note2];
                const t6 = this.chordSolveHelper[i.chordType + '_小三'][note3];
                if (t4 && t5 && t6) {
                    // 解决到大三
                    t4.forEach((alto_t: string) => {
                        t5.forEach((tenor_t: string) => {
                            t6.forEach((bass_t: string) => {
                                const altoEffective = transferEffective(i.alto, alto_t)
                                const tenorEffective = transferEffective(i.tenor, tenor_t)
                                const bassEffective = transferEffective(i.bass, bass_t)
                                if (altoEffective && tenorEffective && bassEffective) {
                                    rst[i.chordType + '_小三'].push([
                                        altoEffective,
                                        tenorEffective,
                                        bassEffective,
                                        i.alto, i.tenor, i.bass,
                                        alto_t, tenor_t, bass_t
                                    ])
                                }
                            })
                        })
                    })
                }
            }
        })
        return removeEmptyArrays(rst);
    }

    // 一次遍历找到所有的排列类型
    public findHarmonies = (soprano: string, bass: string, chordType: string, charIndex: number, n: number) => {
        let matches = [];
        let alto: string = '';
        let tenor: string = '';
        for (let i = 1; i <= 12; i++) {
            alto = transpose(soprano, `-${Interval.fromSemitones(i)}`);
            // 中音和次中音相距不超过八度
            for (let j = 1; j <= 12; j++) {
                tenor = transpose(alto, `-${Interval.fromSemitones(j)}`);
                const noteDistance = Interval.get(distance(tenor, bass));
                const semitons = noteDistance.semitones || 0;
                if (semitons >= 0) {
                    // 次中声部低于了低声部，说明向下过头了，舍弃
                    continue;
                }
                let type = '';
                if (i <= 6 && j <= 6) {
                    type = `紧凑排列(前${i}后${j})`;
                } else if ((i <= 6 && j > 6) || (i > 6 && j <= 6)) {
                    type = `混合排列(前${i}后${j})`;
                } else if (i > 6 && j > 6) {
                    type = `开放排列(前${i}后${j})`;
                }

                // 去除##或bb等
                alto = Note.simplify(alto);
                tenor = Note.simplify(tenor);
                // 根据音区同音转换
                alto = noteEnharmonicByAcc(alto, this.accNotes, charIndex, n);
                tenor = noteEnharmonicByAcc(tenor, this.accNotes, charIndex, n);

                matches.push({ alto, tenor, bass, type, chordType });
            }
        }

        return matches;
    };

    private generateChordSolveHelper = (seventhNotes: string[][], direction: string) => {
        const t = (v: number) => {
            if (Math.abs(v) === 0) return '1P';
            if (Math.abs(v) === 1) return v > 0 ? '2m' : '-2m';
            if (Math.abs(v) === 2) return v > 0 ? '2M' : '-2M';
            if (Math.abs(v) === 5) return v > 0 ? '4P' : '-4P';
            if (Math.abs(v) === 7) return v > 0 ? '5P' : '-5P';
        }
        const rst: any = {}
        seventhNotes.forEach(note => {
            const note1 = Note.pitchClass(note[0])
            const note2 = Note.pitchClass(note[1])
            const note3 = Note.pitchClass(note[2])
            const chordType = note[3]
            if (direction === 'up') {
                if (chordType === '大小七') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-1)]
                    rst[`${chordType}_大三`][note2] = [t(-2)]
                    rst[`${chordType}_大三`][note3] = [t(5), t(-7)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-2)]
                    rst[`${chordType}_小三`][note2] = [t(-2)]
                    rst[`${chordType}_小三`][note3] = [t(5), t(-7)]
                }
                if (chordType === '大小七二转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(0)]
                    rst[`${chordType}_大三`][note2] = [t(-1)]
                    rst[`${chordType}_大三`][note3] = [t(-2)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(0)]
                    rst[`${chordType}_小三`][note2] = [t(-2)]
                    rst[`${chordType}_小三`][note3] = [t(-2)]
                }
                if (chordType === '大小七三转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-2)]
                    rst[`${chordType}_大三`][note2] = [t(0)]
                    rst[`${chordType}_大三`][note3] = [t(-1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-2)]
                    rst[`${chordType}_小三`][note2] = [t(0)]
                    rst[`${chordType}_小三`][note3] = [t(-2)]
                }
                if (chordType === '减七Ⅰ') {
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-1)]
                    rst[`${chordType}_小三`][note2] = [t(-2)]
                    rst[`${chordType}_小三`][note3] = [t(1)]
                }
                if (chordType === '减七Ⅰ二转位') {
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(1)]
                    rst[`${chordType}_小三`][note2] = [t(-1)]
                    rst[`${chordType}_小三`][note3] = [t(-2)]
                }
                if (chordType === '减七Ⅰ三转位') {
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-2)]
                    rst[`${chordType}_小三`][note2] = [t(1)]
                    rst[`${chordType}_小三`][note3] = [t(-1)]
                }
                if (chordType === '减七Ⅱ一转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-1)]
                    rst[`${chordType}_大三`][note2] = [t(-1)]
                    rst[`${chordType}_大三`][note3] = [t(2)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-1)]
                    rst[`${chordType}_小三`][note2] = [t(-2)]
                    rst[`${chordType}_小三`][note3] = [t(1)]
                }
                if (chordType === '减七Ⅱ二转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(2)]
                    rst[`${chordType}_大三`][note2] = [t(-1)]
                    rst[`${chordType}_大三`][note3] = [t(-1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(1)]
                    rst[`${chordType}_小三`][note2] = [t(-1)]
                    rst[`${chordType}_小三`][note3] = [t(-2)]
                }
                if (chordType === '减七Ⅱ三转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-1)]
                    rst[`${chordType}_大三`][note2] = [t(2)]
                    rst[`${chordType}_大三`][note3] = [t(-1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-2)]
                    rst[`${chordType}_小三`][note2] = [t(1)]
                    rst[`${chordType}_小三`][note3] = [t(-1)]
                }
            } else {
                if (chordType === '大小七') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-2)]
                    rst[`${chordType}_大三`][note2] = [t(1)]
                    rst[`${chordType}_大三`][note3] = [t(5), t(-7)]
                }
                if (chordType === '大小七一转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(0)]
                    rst[`${chordType}_大三`][note2] = [t(-2)]
                    rst[`${chordType}_大三`][note3] = [t(1)]
                }
                if (chordType === '大小七二转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(1)]
                    rst[`${chordType}_大三`][note2] = [t(0)]
                    rst[`${chordType}_大三`][note3] = [t(-2)]
                }
                if (chordType === '减七Ⅰ') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-1)]
                    rst[`${chordType}_大三`][note2] = [t(2)]
                    rst[`${chordType}_大三`][note3] = [t(1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-2)]
                    rst[`${chordType}_小三`][note2] = [t(1)]
                    rst[`${chordType}_小三`][note3] = [t(1)]
                }
                if (chordType === '减七Ⅰ一转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(1)]
                    rst[`${chordType}_大三`][note2] = [t(-1)]
                    rst[`${chordType}_大三`][note3] = [t(2)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(1)]
                    rst[`${chordType}_小三`][note2] = [t(-2)]
                    rst[`${chordType}_小三`][note3] = [t(1)]
                }
                if (chordType === '减七Ⅰ二转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(2)]
                    rst[`${chordType}_大三`][note2] = [t(1)]
                    rst[`${chordType}_大三`][note3] = [t(-1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(1)]
                    rst[`${chordType}_小三`][note2] = [t(1)]
                    rst[`${chordType}_小三`][note3] = [t(-2)]
                }
                if (chordType === '减七Ⅱ') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(-1)]
                    rst[`${chordType}_大三`][note2] = [t(2)]
                    rst[`${chordType}_大三`][note3] = [t(1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(-1)]
                    rst[`${chordType}_小三`][note2] = [t(1)]
                    rst[`${chordType}_小三`][note3] = [t(1)]
                }
                if (chordType === '减七Ⅱ一转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(1)]
                    rst[`${chordType}_大三`][note2] = [t(-1)]
                    rst[`${chordType}_大三`][note3] = [t(2)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(1)]
                    rst[`${chordType}_小三`][note2] = [t(-1)]
                    rst[`${chordType}_小三`][note3] = [t(1)]
                }
                if (chordType === '减七Ⅱ三转位') {
                    rst[`${chordType}_大三`] = {}
                    rst[`${chordType}_大三`][note1] = [t(2)]
                    rst[`${chordType}_大三`][note2] = [t(1)]
                    rst[`${chordType}_大三`][note3] = [t(-1)]
                    rst[`${chordType}_小三`] = {}
                    rst[`${chordType}_小三`][note1] = [t(1)]
                    rst[`${chordType}_小三`][note2] = [t(1)]
                    rst[`${chordType}_小三`][note3] = [t(-1)]
                }
            }
        })

        return rst;
    }

    // 开放排列
    public openArrangement = (soprano: string, bass: string): Array<{ alto: string; tenor: string; type: string }> => {
        /**
         * 高声部和中声部的音符从7-12开始遍历，同时判断中声部到低声部中间是否还能容纳一个距离中声部7-12音程的次中声部
         * 找到上述的所有情况，从中随机挑选一个
         * 注意这个过程如果碰到包含升降号的音符，则跳过.
         * 首先从中声部距离高声部7个半音（纯五度，5P）开始
         */
        return this.findMatches(soprano, bass, [7, 12], [7, 12], '开放排列');
    };

    // 混合排列
    public mixArrangement = (soprano: string, bass: string): Array<{ alto: string; tenor: string; type: string }> => {
        /**
         * type: 1: 前松后紧 2. 前紧后松
         */
        const type = Math.floor(Math.random() * 2) + 1;
        const frontDistance = type === 1 ? [7, 12] : [1, 6];
        const backDistance = type === 1 ? [1, 6] : [7, 12];

        return this.findMatches(soprano, bass, frontDistance, backDistance, '混合排列');
    };

    // 紧凑排列
    public narrowArrangement = (soprano: string, bass: string): Array<{ alto: string; tenor: string; type: string }> => {
        return this.findMatches(soprano, bass, [1, 6], [1, 6], '紧凑排列');
    };

    public findMatches = (soprano: string, bass: string, frontDistance: Array<number>, backDistance: Array<number>, type: string) => {
        const matches = [];
        let alto: string = '';
        let tenor: string = '';
        for (let i = frontDistance[0]; i <= frontDistance[1]; i++) {
            alto = transpose(soprano, `-${Interval.fromSemitones(i)}`);
            // 排除包含升降号的音符
            if (Note.accidentals(alto) !== '') {
                continue;
            }
            /**
             * 为次中声部遍历判断了frontDistance[1]次
             * 每次遍历里面还有backDistance[1]次遍历，判断能否找到合适的次中声部
             */
            for (let j = backDistance[0]; j <= backDistance[1]; j++) {
                tenor = transpose(alto, `-${Interval.fromSemitones(j)}`);
                if (Note.accidentals(tenor) !== '') {
                    continue;
                }
                /**
                 * 判断次中声部是否超越了低声部
                 */
                const tenorBassDistance = Interval.get(distance(tenor, bass));
                const semitone = tenorBassDistance.semitones || 0;
                if (semitone < 0) {
                    // 次中音和低音的半音小于0，说明次中音在低音的上面，符合要求
                    matches.push({
                        alto,
                        tenor,
                        bass,
                        type,
                    });
                }
            }
        }

        return matches;
    };

    private adjustChord = (baseNote: string, chord: string) => {
        let _note = chord;
        // 如果二声部骨架靠的太近，导致没办法插入四部和声，就把低声部向下挪一个八度
        const noteDistance = Interval.get(distance(baseNote, chord));
        const semitons = Math.abs(noteDistance.semitones || 0);
        if (semitons < 6) {
            // 6 表示6个半音，一个大三度
            _note = transpose(chord, '-8P');
            console.log(`${baseNote}和${chord}音程过近，调整为${_note}`);
        }
        return _note;
    };

    /**
     *
     * @param melodyDuration
     * @param chordDuration
     * @param n 第几拍
     */
    private matchRequired = (melodyDuration: string, chordDuration: string, n: number) => {
        let staveIndex = -1;
        let changeStave = false;
        if (melodyDuration === '4' && chordDuration === '4') {
            // 一对一的情况每个高音谱都插入音符
            staveIndex = n;
            changeStave = true;
        }
        if (melodyDuration === '4' && chordDuration === '2') {
            staveIndex = n === 0 ? 0 : 2;
            changeStave = true;
        }
        if (melodyDuration === '4' && chordDuration === '1' && n === 0) {
            staveIndex = 0;
            changeStave = true;
        }
        if (melodyDuration === '2' && chordDuration === '2') {
            staveIndex = n;
            changeStave = true;
        }
        if (melodyDuration === '2' && chordDuration === '1' && n === 0) {
            staveIndex = 0;
            changeStave = true;
        }
        if (melodyDuration === '1' && chordDuration === '1') {
            staveIndex = 0;
            changeStave = true;
        }

        return { staveIndex, changeStave };
    };

    private compareChord = (a: any, b: any) => {
        // 定义一个类型到排序权重的映射
        const typeWeights: { [label: string]: number } = {
            原位: 1,
            第一转位: 1,
            第二转位: 3,
        };

        // 首先根据type的权重进行比较
        if (typeWeights[a.type] !== typeWeights[b.type]) {
            return typeWeights[a.type] - typeWeights[b.type];
        }

        // 如果type相同，则根据sort的值进行比较
        return a.semitone - b.semitone;
    };

    private matchDurations = (melodyDuration: string) => {
        const melody_to_chord_num = localStorage.getItem('melody_to_chord_num');
        if (melodyDuration === '4') {
            if (melody_to_chord_num === '2') {
                return ['4', '2'];
            }
            if (melody_to_chord_num === '4') {
                return ['4', '1'];
            }
        }
        if (melodyDuration === '2') {
            if (melody_to_chord_num === '2' || melody_to_chord_num === '4') {
                return ['2', '1'];
            }
        }
        return [];
    };

    private noteIsEqual = (a: string, b: string) => {
        a = Note.pitchClass(a);
        b = Note.pitchClass(b);

        return Note.enharmonic(a) === b || Note.enharmonic(b) === a || a === b;
    };

    private findAccNotes = (melodyNotes: any) => {
        const accNotes: accNotesType[] = [];
        melodyNotes.forEach((melodyNote: any, charIndex: number) => {
            melodyNote.notes.forEach((note: any, n: number) => {
                const key = note.keys[0].split('/')[0];
                const accidentals = note.accidentals.filter((acc: any) => acc.index === 0)[0]?.modifier || '';
                if (accidentals !== '') {
                    accNotes.push({
                        letter: key,
                        acc: accidentals,
                        barIndex: charIndex,
                        noteIndex: n
                    });
                }
            });
        });
        this.accNotes = accNotes;
    }

    private findDetuningInfo = (chordRst: Array<chordNoteBarType>) => {
        /**
         * 出现 >= 2的升降号，从第一个升降号开始到最后都划归为一个音区
         * 这个音区下面的三和弦，不论是配出来的还是通过七和弦解决出来的
         * 都要收到音区内升降号的影响
         * 比如用户输入了#C，三和弦内的C音也要显示#号
         */
        const detuningInfo: detuningInfoType[] = [];
        chordRst.forEach((rst: chordNoteBarType, barIndex: number) => {
            rst.forEach((chordNote: chordNoteType, noteIndex: number) => {
                const currentNote = chordNote.baseNote;
                /*
                出现升降号
                判断下一个音和上一个音是否构成小二度
                判断是上行还是下行
                */
                const [nextNote, nextBarIndex, nextNoteIndex] = this.findNextNote(chordRst, barIndex, noteIndex);
                if (currentNote.length === 3 && nextNote && nextNote.length !== 3) {
                    const nextNoteDistance = distance(currentNote, nextNote);
                    if (nextNoteDistance === '2m') {
                        // 小二度上行
                        detuningInfo.push({
                            barIndex,
                            noteIndex,
                            nextBarIndex,
                            nextNoteIndex,
                            direction: 'up',
                            detuningStart: detuningInfo.length === 0,
                            fromNote: currentNote,
                            toNote: nextNote,
                        });
                    }
                    if (nextNoteDistance === '-2m') {
                        // 小二度下行
                        detuningInfo.push({
                            barIndex,
                            noteIndex,
                            nextBarIndex,
                            nextNoteIndex,
                            direction: 'down',
                            detuningStart: detuningInfo.length === 0,
                            fromNote: currentNote,
                            toNote: nextNote,
                        });
                    }
                }
            });
        });
        return detuningInfo;
    };

    private findNextNote = (chordRst: Array<chordNoteBarType>, barIndex: number, noteIndex: number): [string, number, number] => {
        if (chordRst[barIndex][noteIndex + 1]) {
            return [chordRst[barIndex][noteIndex + 1].baseNote, barIndex, noteIndex + 1];
        } else if (chordRst[barIndex + 1]) {
            return [chordRst[barIndex + 1][0].baseNote, barIndex + 1, 0];
        } else {
            return ['', -1, -1];
        }
    };

    private findPreviousItem = (arrItem: any[][], barIndex: number, noteIndex: number) => {
        if (arrItem[barIndex][noteIndex - 1]) {
            return arrItem[barIndex][noteIndex - 1];
        } else if (arrItem[barIndex - 1]) {
            const len = arrItem[barIndex - 1].length;
            return arrItem[barIndex - 1][len - 1];
        } else {
            return '';
        }
    };

    private findNextItem = (arrItem: any[][], barIndex: number, noteIndex: number) => {
        if (arrItem[barIndex][noteIndex + 1]) {
            return arrItem[barIndex][noteIndex + 1];
        } else if (arrItem[barIndex + 1]) {
            return arrItem[barIndex + 1][0];
        } else {
            return '';
        }
    };

    private matchMelody = (charIndex: number) => {
        const melodyStaves = formatStavesToJson(this.melodyStaveList);
        const rst: any = [];
        melodyStaves[charIndex].notes.forEach((note) => {
            const key = note.keys[0].split('/')[0];
            const octave = parseInt(note.keys[0].split('/')[1]);
            const accidentals = note.accidentals[0] ? note.accidentals[0].modifier : '';
            rst.push({
                notes: [`${key}${accidentals}${octave}`],
                duration: note.duration,
            });
        });
        return rst;
    }

    private findHarmonyMatches = (soprano: string, chordNotes: string[][], charIndex: number, n: number) => {
        let rst: any[] = [];

        chordNotes.forEach((chordNote: any[]) => {
            const bass = chordNote[0];
            const secondNote = chordNote[1];
            const thirdNote = chordNote[2];
            const chordType = chordNote[3];

            let matches = [];
            matches = this.findHarmonies(soprano, bass, chordType, charIndex, n);
            // 从结果集中匹配和弦音
            rst = [
                ...rst,
                ...matches.filter((item: any) => {
                    return (
                        (this.noteIsEqual(item.alto, secondNote) && this.noteIsEqual(item.tenor, thirdNote)) ||
                        (this.noteIsEqual(item.alto, thirdNote) && this.noteIsEqual(item.tenor, secondNote))
                    );
                }),
            ];
        });

        return rst;
    }

    private vocalSort = (rst: any, previousAlto: string, previousTenor: string) => {
        let alto = '';
        let tenor = '';
        let bass = '';
        let type = '';
        let chordType = '';
        /**
         * 匹配思路：
         * 遍历一遍所有的四部和声组合
         * 把每一个组合里面的 中音 和次中音 和上一个四部和声的 中音和次中音 的音程关系找出来
         * 找到最符合的组合
         *  1. 优先原位和第一转位
         *  2. 要么是音程关系的和最小
         *  3. 要么是有2尽量拿2, 有3尽量拿3, 以此类推
         * 如果一个都没找到怎么办
         *  1. 拿一个 音程关系求和 最小的
         */
        const semitoneRst = [];
        ({ alto, tenor, bass, type, chordType } = rst[0]);
        for (let i = 0; i < rst.length; i++) {
            ({ alto, tenor, bass, type } = rst[i]);
            const altoSemiton = Math.abs(Interval.get(distance(alto, previousAlto)).semitones || 0);
            const tenorSemiton = Math.abs(Interval.get(distance(tenor, previousTenor)).semitones || 0);
            semitoneRst.push({
                altoSemiton,
                tenorSemiton,
                semitone: altoSemiton + tenorSemiton,
                alto,
                tenor,
                bass,
                type,
                chordType,
            });
        }

        // 优先原位和第一转位，再按照音程关系排序
        semitoneRst.sort(this.compareChord);

        return semitoneRst;
    }

    private test = () => {
        // console.log(Chord.detect(['F', 'A', 'D']))
        // console.log('distance', distance('D#4', 'C5'));
        // console.log('transpose', transpose('C4', '1P'));
        // console.log('distance', distance('G4', 'C5'));
        // console.log('distance', distance('F4', 'D4'));
        // console.log('distance', distance('D5', 'F5'));
        // console.log(findChordDetect('B', 'D', 'G'))
        // console.log(Interval.get(distance('C2', 'C4')));
        // console.log(Note.pitchClass('C#4'))
        // console.log(Note.enharmonic('Ab3'))
        // const daXiaoQiRoot = transpose('G#4', '-3M');
        // const daXiaoQiWuYin = transpose(daXiaoQiRoot, '5P');
        // const daXiaoQiQiYin = transpose(daXiaoQiRoot, '7m');
        // console.log(daXiaoQiRoot, 'G#4', daXiaoQiWuYin, daXiaoQiQiYin)
        // console.log(Note.simplify('E#4'))
        // console.log(noteMatchAcc('D#2', this.accNotes))
        // console.log('distance', distance('E3', 'Db4'))
        // console.log('distance', transpose('E3', '3M'))
        // console.log('distance', distance('C', 'F'));
        // ['C', 'D', 'E', 'F', 'G', 'A', 'B'].forEach((note: string) => console.log(transpose(note, '4P')))
        // console.log(Note.simplify(transpose('Bbb4', '-3M')))
        // console.log(Note.simplify('Cn4'))
        const interval = Interval.get(distance('D#5', 'C4'));
        const relation = Interval.simplify(Interval.fromSemitones(interval.semitones as number));
        // console.log(relation)
        // console.log(transpose( 'D#4', '-6m'))
        // console.log(simplifyRelation( 'G3', 'D#4'));
        // console.log(simplifyRelation('G3', 'D#4'));
    };

}
